import React from 'react';
import { FormLabel, Table } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import PropTypes from 'prop-types';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';

const isMobile = () => window.innerWidth <= 768;

const FormReport = ({
  metadata,
  selectedRows,
  handleSelectRow,
  formValues,
  fetchData, // Optional: server-side fetch function
  serverSide = false, // If true, expects fetchData to be used
  totalCount, // Total row count for server-side
  loading // Show loading indicator for server-side
}) => {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const defaultLimit = metadata?.defaultPageSize || 10;
  const [limit] = React.useState(defaultLimit); // Fixed limit

  // For server-side pagination, keep current data in state
  const [serverData, setServerData] = React.useState([]);
  // For client-side, use local slice of formData
  const formData = metadata?.options || [];

  const allParentsSelected =
    metadata.dependsOn?.every(
      dep =>
        formValues[dep] !== undefined &&
        formValues[dep] !== null &&
        formValues[dep] !== ''
    ) ?? true;

  React.useEffect(() => {
    if (serverSide && fetchData) {
      fetchData({ page, limit, search }).then(({ data, total }) => {
        setServerData(data);
      });
    }
  }, [page, limit, search, fetchData, serverSide]);

  if (!allParentsSelected || (formData.length === 0 && !serverSide)) {
    return null;
  }

  // React.useEffect(() => {
  //   if (onSearch && search && search.trim().length > 0) {
  //     onSearch(search);
  //   }
  //   // Do not call onSearch if search is empty
  // }, [search, onSearch]);

  function formatLabel(label) {
    let result = label.replaceAll('_', ' ');
    result = result
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return result;
  }

  // Get all keys except 'id'
  const dataKeys = Object.keys(
    (serverSide ? serverData[0] : formData[0]) || {}
  ).filter(key => key !== 'id');

  // Paginate data
  const pagedData = serverSide
    ? serverData
    : formData.slice(page * limit, page * limit + limit);

  // Pagination controls
  const rowCount = serverSide ? totalCount : formData.length;
  const canPreviousPage = page > 0;
  const canNextPage = (page + 1) * limit < rowCount;

  // --- Mobile rendering (unchanged) ---
  if (isMobile()) {
    return (
      <div className="form-report-mobile">
        <FormLabel>
          {metadata.label}
          {selectedRows.length > 0 && ` (${selectedRows.length} Selected)`}
        </FormLabel>
        {loading
          ? Array.from({ length: limit }).map((_, i) => (
              <div key={i}>Loading...</div>
            ))
          : pagedData.map((row, idx) => (
              <div
                key={row.id || idx}
                className="form-report-card"
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  marginBottom: 16,
                  padding: 16,
                  display: 'flex',
                  alignItems: 'flex-start',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedRows.includes(row['id'])}
                  onChange={() => handleSelectRow(metadata, row['id'])}
                  style={{ marginRight: 16, marginTop: 4 }}
                />
                <div
                  style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}
                >
                  {dataKeys.map((key, i) => (
                    <div
                      key={key}
                      style={{
                        flex: '1 1 45%',
                        minWidth: 0,
                        textAlign: i % 2 === 0 ? 'left' : 'right',
                        marginBottom: 2
                      }}
                    >
                      {/* <div
                    style={{
                      fontWeight: 500,
                      color: '#888',
                      fontSize: 12,
                      marginBottom: 2
                    }}
                  >
                    {formatLabel(key)}
                  </div> */}
                      <div
                        style={{
                          fontSize: 15,
                          color: '#222',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                        title={row[key] || '-'}
                      >
                        {row[key] && String(row[key]).length > 12
                          ? String(row[key]).slice(
                              0,
                              Math.floor(String(row[key]).length / 2)
                            ) + '...'
                          : row[key] || '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        {/* Keep simple controls for mobile for now */}
        <div className="mt-3">
          <ServerSideTableFooter
            rowCount={rowCount}
            pageSize={limit}
            pageIndex={page}
            setPageSize={() => {}} // not needed
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            nextPage={() => setPage(page + 1)}
            previousPage={() => setPage(Math.max(0, page - 1))}
            navButtons
            onPageChange={setPage}
          />
        </div>
      </div>
    );
  }

  // --- Desktop/table rendering ---
  return (
    <div className="form-report">
      {/* <div className="d-flex flex-column flex-md-row gap-3">
        <input
          type="text"
          placeholder="Search..."
          className="form-control w-100"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div> */}
      <FormLabel>
        {metadata.label}
        {selectedRows.length > 0 && ` (${selectedRows.length} Selected)`}
      </FormLabel>
      <Table striped bordered hover responsive className="w-100">
        <thead>
          <tr>
            {pagedData.length > 0 && <th>Select</th>}
            {dataKeys.map(key => (
              <th key={key}>{formatLabel(key)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={dataKeys.length + 1}>Loading...</td>
            </tr>
          ) : (
            pagedData.map((row, index) => (
              <tr key={row.id || index}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row['id'])}
                    onChange={() => handleSelectRow(metadata, row['id'])}
                  />
                </td>
                {dataKeys.map(key => (
                  <td key={key}>{row[key] || '-'}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      <div className="mt-3">
        <ServerSideTableFooter
          rowCount={rowCount}
          pageSize={limit}
          pageIndex={page}
          setPageSize={() => {}} // not needed
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          nextPage={() => setPage(page + 1)}
          previousPage={() => setPage(Math.max(0, page - 1))}
          navButtons
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

FormReport.defaultProps = {
  metadata: {
    options: [],
    defaultPageSize: 10
  },
  selectedRows: [],
  handleSelectRow: () => {}
  // onSearch: undefined
};

FormReport.propTypes = {
  metadata: PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.object),
    label: PropTypes.string,
    dependsOn: PropTypes.arrayOf(PropTypes.string),
    defaultPageSize: PropTypes.number
  }),
  selectedRows: PropTypes.arrayOf(PropTypes.number),
  handleSelectRow: PropTypes.func,
  formValues: PropTypes.object,
  fetchData: PropTypes.func,
  serverSide: PropTypes.bool,
  totalCount: PropTypes.number,
  loading: PropTypes.bool
};

export default FormReport;
